import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { createEditorExtensions } from "./editor/editor-extensions";
import { EditorBubbleMenu } from "./editor/bubble-menu/editor-bubble-menu";
import { ImageBubbleMenu } from "./editor/bubble-menu/image-bubble-menu";
import { BlockDragHandle } from "./editor/drag-handle/block-drag-handle";

interface ChangelogEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  onImageUpload?: (file: File) => Promise<string>;
  /** handbook 같이 각주 기능이 필요한 곳에서 true. changelog 본진에서는 default off. */
  enableFootnotes?: boolean;
  /**
   * bubble menu 의 각주 버튼이 호출. 부모는 새 footnote 를 footnotes 배열에 추가하고
   * 그 id 를 반환. 이 컴포넌트가 editor 의 insertFootnote 커맨드로 본문 마커 인서트.
   */
  onInsertFootnote?: () => string;
}

/** 부모가 ref 로 호출할 수 있는 imperative 핸들. footnote 외부 버튼이 본문 마커 인서트 가능. */
export interface ChangelogEditorHandle {
  /** id 를 받아 cursor 위치에 footnote sup 마커 삽입. */
  insertFootnoteMarker: (id: string) => void;
  /** editor 가 focus 상태인지 — 없으면 cursor 가 본문 끝에 가서 insert 됨. */
  focus: () => void;
}

export const ChangelogEditor = forwardRef<
  ChangelogEditorHandle,
  ChangelogEditorProps
>(function ChangelogEditor(
  {
    content,
    onChange,
    placeholder = "/ 를 입력하여 옵션을 탐색하세요",
    onImageUpload,
    enableFootnotes,
    onInsertFootnote,
  },
  ref,
) {
  const [mounted, setMounted] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleCreate = useCallback(() => {
    setMounted(true);
  }, []);

  const editor = useEditor({
    extensions: createEditorExtensions({
      placeholder,
      onImageUpload,
      enableFootnotes,
    }),
    content,
    onCreate: handleCreate,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (editor.getHTML() === content) return;

    editor.commands.setContent(content, { emitUpdate: false });
  }, [editor, content]);

  useImperativeHandle(
    ref,
    () => ({
      insertFootnoteMarker: (id: string) => {
        if (!editor || editor.isDestroyed) return;
        editor.chain().focus().insertFootnote(id).run();
      },
      focus: () => {
        editor?.chain().focus().run();
      },
    }),
    [editor],
  );

  if (!editor) return null;

  return (
    <div ref={wrapperRef} className="relative">
      <EditorContent
        editor={editor}
        className="max-w-none pr-4 py-2 text-base focus-within:outline-none [&_.ProseMirror]:min-h-[300px] [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground/50 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0"
      />
      {mounted && (
        <>
          <EditorBubbleMenu
            editor={editor}
            onInsertFootnote={
              enableFootnotes && onInsertFootnote
                ? () => {
                    const id = onInsertFootnote();
                    editor.chain().focus().insertFootnote(id).run();
                  }
                : undefined
            }
          />
          <ImageBubbleMenu editor={editor} />
          <BlockDragHandle editor={editor} wrapperRef={wrapperRef} />
        </>
      )}
    </div>
  );
});
